import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { EmployerLeaveYearRuleDialog, LeaveHoursBank, LeaveYearRuleForm, LeaveYearRulePost, RestoreLeaveType } from '@core/models/leave-admin/employers/employer-leave-year-rule.model';
import { EmployerProfile, LeaveYearRule } from '@core/models/leave-admin/employers/employer-profile.model';
import { ErrorService, LayoutService } from '@core/services';
import { EmployerProfileStoreService } from '@core/services/employer-profile-store.service';
import { indicate, nameof, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { Observable, Subject } from 'rxjs';
import { LeaveYearRulesMode } from '@core/enums/leave-year-rule-mode.enum';
import { ManageEmployersService } from '@core/services/manage-employers.service';
import { ModelStateErrors, SelectionOption, StiiraError } from '@core/models';
import { takeUntil } from 'rxjs/operators';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { LeaveYear } from '@core/enums/leave-years.enum';
import { UpdateConfirmationDialog } from '@core/models/shared/update-confirmation-dialog.model';
import { UpdateConfirmationComponent } from '../update-confirmation/update-confirmation.component';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { DurationType } from '@core/enums/duration-type.enum';
import { MeasuredForwardStart } from '@core/enums/measured-forward-start.enum';

@Component({
    selector: 'app-add-edit-leave-year-rule',
    templateUrl: './add-edit-leave-year-rule.component.html',
    styleUrls: ['./add-edit-leave-year-rule.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddEditLeaveYearRulesComponent implements OnInit {
  static readonly componentName = 'AddEditLeaveYearRulesComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();

  public form: FormGroup<LeaveYearRuleForm>;
  public isSubmitting$: Subject<boolean> = new Subject<boolean>();
  public leaveYearRulesMode = LeaveYearRulesMode;
  public selectedBank: LeaveHoursBank;
  public bankOptions: SelectionOption[] = [];
  public durationTypeOptions: SelectionOption[] = [];
  public measuredForwardStartOptions: SelectionOption[] = [];
  public restoreLeaveTypeOptions: RestoreLeaveType[] = [];
  public position = position;
  public onDragEnded = onDragEnded;
  public leaveYear = LeaveYear;

  private formChangeEmitted: boolean = false;
  private formInitValues: any;
  private destroy$: Subject<void> = new Subject<void>();
  private durationType = DurationType;
  private measuredForwardStart = MeasuredForwardStart

  public isMobile$: Observable<boolean>;

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      employerId: number;
      dialogOptions: EmployerLeaveYearRuleDialog;
      sysText: any;
      updateConfSysText: any;
      mode: LeaveYearRulesMode;
      // used for edit...
      leaveYearRuleData?: LeaveYearRule;
      modelStateErrors?: ModelStateErrors;
      modelStateIndex?: number;
    },
    private fb: FormBuilder,
    private layoutService: LayoutService,
    private dialogRef: MatDialogRef<AddEditLeaveYearRulesComponent>,
    private store: EmployerProfileStoreService,
    private dialog: MatDialog,
    private service: ManageEmployersService,
    private errorService: ErrorService
  ) { 
    this.form = this.fb.group<LeaveYearRuleForm>({
      effectiveDate:  this.fb.control(null),
      bank:  this.fb.control(null, Validators.required),
      workingInState:  this.fb.control(null),
      leaveYear:  this.fb.control(null, Validators.required),
      duration: this.fb.control(null),
      durationType: this.fb.control(null),
      measuredForwardStart: this.fb.control(null),
      fixedStart: this.fb.control(null),
      restoreHours: this.fb.control(null),
      restoreConditions: this.fb.control(null),
      followFmlaUsageRules: this.fb.control(false),
      isFullWeekClosureWorkdaysOnly: this.fb.control(true),
      isFullWeekAbsenceWorkdaysOnly: this.fb.control(true),
    });
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.close(false));

    this.setupForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSubmit(overrideUpdateConf: boolean = false): void {
    this.form.markAsUntouched();
    const dto: LeaveYearRulePost = {
      overrideUpdateConf: overrideUpdateConf,
      employerId: this.data.employerId,
      effectiveDate: this.form.controls.effectiveDate.value,
      bank: this.form.controls.bank.value,
      workingInStateId: this.form.controls.workingInState.value,
      leaveYear: this.form.controls.leaveYear.value,
      duration: this.form.controls.duration.value,
      durationType: this.form.controls.durationType.value,
      measuredForwardStart: this.form.controls.measuredForwardStart.value,
      fixedStart: this.form.controls.fixedStart.value,
      restoreHours: this.form.controls.restoreHours.value,
      restoreConditions: this.form.controls.restoreHours.value == true
        ? this.form.controls.restoreConditions.value
        : null,
      
      followFmlaUsageRules: this.form.controls.followFmlaUsageRules.value,
      isFullWeekClosureWorkdaysOnly: this.form.controls.followFmlaUsageRules.value ?
        this.form.controls.isFullWeekClosureWorkdaysOnly.value : null,
      isFullWeekAbsenceWorkdaysOnly: this.form.controls.followFmlaUsageRules.value ?
        this.form.controls.isFullWeekAbsenceWorkdaysOnly.value : null
    }

    let postObs: Observable<EmployerProfile | UpdateConfirmationDialog>;
    if (this.data.mode == LeaveYearRulesMode.Add) {
      postObs = this.service.postLeaveYearRulesAdd(dto);
    } else if (this.data.mode == LeaveYearRulesMode.Edit) {
      dto.leaveYearRuleId = this.data.leaveYearRuleData.leaveYearRuleId;
      postObs = this.service.postLeaveYearRulesEdit(dto)
    }

    postObs
      .pipe(indicate(this.isSubmitting$))
      .subscribe(
        (res) => {
          if (res.showUpdateConfirmationDialog) {
            this.openUpdateConfirmationDialog(res as UpdateConfirmationDialog);
          }
          else {
            this.store.employerProfile = res as EmployerProfile;
            this.dialogRef.close();
          }
        },
        (err: StiiraError) => {
          this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors);
        })
  }

  public close(canNavigate: boolean): void {
    if (canNavigate) {
      this.dialogRef.close();
    } else {
      if (this.noChanges) {
        this.dialogRef.close();
      } else {
        this.openUnsavedChangesDialog();
      }
    }
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.store.sysText.unsavedChanges,
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }

  private setupForm(): void {
    let msErrors = null;

    this.buildBankOptions();
    this.buildDurationTypeOptions(this.data.leaveYearRuleData?.leaveYear.id);
    this.buildMeasuredForwardStartOptions();

    if (this.data.mode == LeaveYearRulesMode.Edit) {
      this.form.patchValue({
        effectiveDate: this.data.leaveYearRuleData.effectiveDate,
        bank: this.data.leaveYearRuleData.bank as number,
        workingInState: this.data.leaveYearRuleData.workingInState?.id as number ?? null,
        leaveYear: this.data.leaveYearRuleData.leaveYear?.id as number,
        duration: this.data.leaveYearRuleData.duration,
        durationType: this.data.leaveYearRuleData.durationType,
        measuredForwardStart: this.data.leaveYearRuleData.measuredForwardStart,
        fixedStart: this.data.leaveYearRuleData.fixedStart,
        restoreHours: this.data.leaveYearRuleData.restoreHours,
        restoreConditions: this.data.leaveYearRuleData.restoreConditions?.map(rc => rc.id as string),

        followFmlaUsageRules: this.data.leaveYearRuleData.followFmlaUsageRules,
        isFullWeekClosureWorkdaysOnly: this.data.leaveYearRuleData.isFullWeekClosureWorkdaysOnly ?? true,
        isFullWeekAbsenceWorkdaysOnly: this.data.leaveYearRuleData.isFullWeekAbsenceWorkdaysOnly ?? true
      });

      if (!this.data.leaveYearRuleData.followFmlaUsageRules) {
        this.form.controls.isFullWeekClosureWorkdaysOnly.disable();
        this.form.controls.isFullWeekAbsenceWorkdaysOnly.disable();
      }

      this.selectedBank = this.data.dialogOptions.leaveHoursBanks
        .find(b => b.id == this.data.leaveYearRuleData.bank) ?? null;

      this.setRestoreLeaveTypeOptions(
        this.selectedBank.restoreConditions, 
        (this.data.leaveYearRuleData.workingInState?.id as number) ?? null);

      // leave year rules msErrors are formatted for the table (array)
      // build msErrors for selected rules by modelStateIndex 
      msErrors = {};
      Object.keys(this.data.modelStateErrors).forEach((err) => {
        if (err.includes(`${nameof<EmployerProfile>('leaveYearRules')}.${this.data.modelStateIndex}`)) {
          const fieldName = err.substring(err.lastIndexOf('.') + 1);
          msErrors[fieldName] = this.data.modelStateErrors[err];
        }
      });

      this.setDurationField();
      this.setFixedStartField();
      this.setMeasuredForwadStartField();
      this.setRestoreHoursField(this.form.controls.restoreHours, this.data.leaveYearRuleData.effectiveDate, this.form.controls.bank.value);
    } else {
      this.form.controls.measuredForwardStart.disable();
      this.form.controls.fixedStart.disable();
      this.form.controls.restoreHours.disable();
      this.form.controls.isFullWeekClosureWorkdaysOnly.disable();
      this.form.controls.isFullWeekAbsenceWorkdaysOnly.disable();
    }

    this.formInitValues = JSON.parse(JSON.stringify(this.form.value));

    setTimeout(() => {
      if (msErrors) {
        this.errorService.setFormModelStateErrors(this.form, msErrors);
      }

      if (this.data.leaveYearRuleData?.workingInState?.id) {
        this.handleStateChange(this.data.leaveYearRuleData.workingInState?.id as number);
      }

      this.form.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.formChangeEmitted && !this.noChanges) {
            this.isEditing.emit(true);
            this.formChangeEmitted = true;
          } else if (this.noChanges) {
            this.isEditing.emit(false);
            this.formChangeEmitted = false;
          }
        });

      this.form.controls.effectiveDate.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          this.setRestoreHoursField(this.form.controls.restoreHours, res, this.form.controls.bank.value);
        });

      this.form.controls.bank.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          this.handleBankChange(res);
        });

      this.form.controls.workingInState.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          this.handleStateChange(res);
          
        });

      this.form.controls.leaveYear.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.setDurationField();
          this.setFixedStartField();
          this.setMeasuredForwadStartField();
        });

      this.form.controls.durationType.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.setDurationField();
        });

      this.form.controls.restoreHours.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          if (!res){
            this.form.controls.restoreConditions.setValue(null);
          }
        });

      this.form.controls.followFmlaUsageRules.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.handleFmlaUsageChange(res);
      });
    }, 0);
  }

  private setFixedStartField() {
    if (this.form.controls.leaveYear.value == LeaveYear.Fixed) {
      if (!this.form.controls.fixedStart.enabled){
        this.form.controls.fixedStart.enable();
        this.form.controls.fixedStart.addValidators(Validators.required);
      }
    } else {
      this.form.controls.fixedStart.setValue(null);
      this.form.controls.fixedStart.removeValidators(Validators.required);
      this.form.controls.fixedStart.disable();
    }
  }

  private setMeasuredForwadStartField() {
    if (this.form.controls.leaveYear.value == LeaveYear.FromFirstUsage) {
      if (!this.form.controls.measuredForwardStart.enabled){
        this.form.controls.measuredForwardStart.enable();
        this.form.controls.measuredForwardStart.addValidators(Validators.required);
      }
    } else {
      this.form.controls.measuredForwardStart.setValue(null);
      this.form.controls.measuredForwardStart.removeValidators(Validators.required);
      this.form.controls.measuredForwardStart.disable();
    }
  }

  private setDurationField() {
    // TODO
    // if (this.form.controls.durationType.value != DurationType.Lifetime) {
    //   if (!this.form.controls.duration.enabled){
    //     this.form.controls.duration.enable();
    //     this.form.controls.duration.addValidators(Validators.required);
    //   }
    // } else {
    //   this.form.controls.duration.setValue(null);
    //   this.form.controls.duration.removeValidators(Validators.required);
    //   this.form.controls.duration.disable();
    // }

    this.buildDurationTypeOptions(this.form.controls.leaveYear.value);
  }

  private handleBankChange(bankId: number): void {
    this.selectedBank = this.data.dialogOptions.leaveHoursBanks.find(b => b.id == bankId) ?? null;
    this.form.controls.restoreConditions.setValue(null);
    this.setRestoreHoursField(this.form.controls.restoreHours, this.form.controls.effectiveDate.value, bankId);
    this.setRestoreLeaveTypeOptions(
      this.selectedBank?.restoreConditions ?? [],
      this.form.controls.workingInState.value ?? null);
  }

  private handleStateChange(stateId: number): void {
    // update bank options with state names if applicable
    const bankId = +this.form.controls.bank.getRawValue();
    this.bankOptions.forEach(bo => {
      const bank = this.data.dialogOptions.leaveHoursBanks.find(lhb => lhb.id === bo.id);
      const lhbsn = bank?.stateNames.find(sn => sn.stateId === stateId);
      if (lhbsn != null) {
        bo.description = lhbsn.stateSpecificName;
        this.form.controls.bank.setValue(null, {emitEvent: false});
        setTimeout(() => {
          this.form.controls.bank.setValue(bankId, {emitEvent: false});
        }, 0);
      } else {
        bo.description = bank.bankName;
        this.form.controls.bank.setValue(null, {emitEvent: false});
        setTimeout(() => {
          this.form.controls.bank.setValue(bankId, {emitEvent: false});
        }, 0);
      }
    });

    if (this.selectedBank) {
      this.setRestoreLeaveTypeOptions(this.selectedBank.restoreConditions, stateId);
    }
  }

  private setRestoreHoursField(restoreHoursControl: FormControl<boolean>, effectiveDate: Date, bankId: number): void {
    if ((effectiveDate == null || bankId == null) && restoreHoursControl.enabled) {
      restoreHoursControl.setValue(null, {emitEvent: false});
      this.form.controls.restoreConditions.setValue(null);
      restoreHoursControl.disable();
    } else if (effectiveDate != null && bankId != null && !restoreHoursControl.enabled) {
      restoreHoursControl.enable();
    }
  }

  private openUpdateConfirmationDialog(updateConfirmationDialog: UpdateConfirmationDialog): void {
    const dialogConfig: MatDialogConfig = {
      id: UpdateConfirmationComponent.componentName,
      width: "450px",
      panelClass: "mat-dialog-container-mobileWidth",
      disableClose: false,
      data: {
        sysText: this.data.updateConfSysText,
        updateConfirmationDialog: updateConfirmationDialog
      }
    };

    this.dialog.open(UpdateConfirmationComponent, dialogConfig)
      .beforeClosed().subscribe((res: boolean) => {
        if (res) {
          this.onSubmit(true);
        }
      });
  }

  private handleFmlaUsageChange(res: boolean): void {
    if (!res){
      this.form.controls.isFullWeekClosureWorkdaysOnly.disable();
      this.form.controls.isFullWeekAbsenceWorkdaysOnly.disable();
    } else {
      this.form.controls.isFullWeekClosureWorkdaysOnly.enable();
      this.form.controls.isFullWeekAbsenceWorkdaysOnly.enable();
    }
  }

  private setRestoreLeaveTypeOptions(allBankRestoreOptions: RestoreLeaveType[], stateId: number = null): void {
    const primaryNameVersions = allBankRestoreOptions.filter(o => o.stateId === null);
    if (stateId === null) {
      this.restoreLeaveTypeOptions = primaryNameVersions;
      return;
    }
    const stateFilteredOptions: RestoreLeaveType[] = [];
    primaryNameVersions.forEach(pnv => {
      const stateVersion = allBankRestoreOptions.find(o => {
        return o.id === pnv.id && o.stateId === stateId;
      });
      if (stateVersion) {
        stateFilteredOptions.push(stateVersion) // get state version if exists
      } else {
        stateFilteredOptions.push(pnv); // otherwise get primary version
      }
    })
    this.restoreLeaveTypeOptions = stateFilteredOptions;
  }

  private buildBankOptions(): void {
    this.data.dialogOptions.leaveHoursBanks.forEach(lhb => {
      const option: SelectionOption = {
        id: lhb.id,
        description: lhb.bankName
      };
      this.bankOptions.push(option);
    });
  }

  private buildDurationTypeOptions(leaveYear): void {
    var durationTypeOptions: SelectionOption[] = [];

    const durationTypeIds = Object.values(this.durationType).filter(v => typeof v === 'number');

    durationTypeIds.forEach(id => {
      if (leaveYear == null
        || this.data.dialogOptions.leaveYearDurationTypes.find(lydt => leaveYear == lydt.leaveYear && id == lydt.durationType)) {
        const desc: string = this.data.sysText['durationTypes_' + id]
        const option: SelectionOption = {
          id: id,
          description: desc
        };

        durationTypeOptions.push(option);
      }
    });

    this.durationTypeOptions = durationTypeOptions;
  }

  private buildMeasuredForwardStartOptions(): void {
    const firstUsageIds = Object.values(this.measuredForwardStart).filter(v => typeof v === 'number');
    firstUsageIds.forEach(id => {
      const desc: string = this.data.sysText['measuredForwardStart_' + id]
      const option: SelectionOption = {
        id: id,
        description: desc
      };
      this.measuredForwardStartOptions.push(option);
    });
  }
}